import type { AxiosError } from 'axios';
import type { ApiErrorBody } from '@/types/api';

/** Stable error kinds the UI branches on. */
export type ApiErrorKind =
  | 'validation'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  | 'downstream_unavailable'
  | 'network'
  | 'unknown';

/** Normalised error carried through the whole UI (never a raw AxiosError). */
export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, unknown>;
  /** Field-level validation errors (problem+json `errors`), when the server sent them. */
  readonly fieldErrors: Array<{ field: string; message: string }>;

  constructor(
    kind: ApiErrorKind, status: number, code: string, message: string,
    details?: Record<string, unknown>, fieldErrors: Array<{ field: string; message: string }> = [],
  ) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.code = code;
    this.details = details;
    this.fieldErrors = fieldErrors;
  }
}

function kindForStatus(status: number): ApiErrorKind {
  switch (status) {
    case 400: return 'validation';
    case 401: return 'unauthenticated';
    case 403: return 'forbidden';
    case 404: return 'not_found';
    case 409: return 'conflict';
    case 429: return 'rate_limited';
    case 502: case 503: case 504: return 'downstream_unavailable';
    default: return status >= 400 ? 'unknown' : 'unknown';
  }
}

/** Plain-language fallback per error kind — never the raw transport message. */
const FRIENDLY: Record<ApiErrorKind, string> = {
  validation: 'Some of the information provided is not valid. Please review it and try again.',
  unauthenticated: 'Your session has expired. Please sign in again.',
  forbidden: 'You do not have permission to perform this action.',
  not_found: 'The requested item could not be found.',
  conflict: 'This change conflicts with an existing record.',
  rate_limited: 'Too many requests. Please wait a moment and try again.',
  downstream_unavailable: 'This service is temporarily unavailable. Please try again shortly.',
  network: 'Network error — please check your connection.',
  unknown: 'Something went wrong. Please try again.',
};

/**
 * Convert any Axios failure into a typed ApiError. Backends answer with RFC 7807
 * problem+json (`detail`/`title`) or Nest-style `{ message }`; 4xx details are
 * user-actionable and shown, 5xx details are internal and replaced.
 */
export function toApiError(error: AxiosError<ApiErrorBody>): ApiError {
  if (error.response) {
    const { status, data } = error.response;
    const kind = kindForStatus(status);
    const body = (data && typeof data === 'object' ? data : {}) as ApiErrorBody & {
      detail?: unknown; title?: unknown; message?: unknown; errors?: unknown;
    };
    const fieldErrors = Array.isArray(body.errors)
      ? body.errors.filter((e): e is { field: string; message: string } =>
        !!e && typeof e === 'object' && typeof (e as { field?: unknown }).field === 'string'
        && typeof (e as { message?: unknown }).message === 'string')
      : [];
    const raw = [body.detail, body.message].find((v) => typeof v === 'string' && v.trim().length > 0)
      ?? (Array.isArray(body.message) ? body.message.filter((v) => typeof v === 'string').join('. ') : undefined);
    const safe = status < 500 && status !== 404 && typeof raw === 'string' && raw.length <= 300
      && !/request failed with status code|cannot (get|post|put|patch|delete) \//i.test(raw);
    return new ApiError(
      kind,
      status,
      typeof body.code === 'string' ? body.code : 'ERROR',
      safe ? raw : FRIENDLY[kind],
      body.details,
      status < 500 ? fieldErrors : [],
    );
  }
  // No response = network/timeout.
  return new ApiError('network', 0, 'NETWORK', FRIENDLY.network);
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/**
 * The user-facing message for a failed mutation/query: the ApiError message when
 * we have one, otherwise the caller's fallback. Shared by every feature's hooks
 * (previously copy-pasted as a local `msg()` helper in eleven files).
 */
export function apiErrorMessage(e: unknown, fallback: string): string {
  return isApiError(e) ? e.message : fallback;
}
